import { useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Map as IMap } from 'immutable';

import { handleError } from '../utils/utils';

import { UpdateStatus } from '../types/enums/updateStatus.enum';

import { SpreadsheetProfile } from '../types/accountTypes';

type ProfileId = SpreadsheetProfile['profileId'];

export type ProfileUpdateStatus = {
    status: UpdateStatus;
    error?: string;
};

export enum ProfileGroups {
    TocaBoca = 'Toca Boca',
    Superfans = 'Superfans',
    KidsPanel = 'Kids Panel',
    Influencers = 'Influencers',
    SpinMaster = 'Spin Master',
    Originator = 'Originator',
    SagoMini = 'Sago Mini',
    MelissaAndDoug = 'Melissa and Doug',
    TocaBocaFamily = 'Toca Boca Family',
    Other = 'Other',
}

export function useProfileGroupsFromGoogleSheet() {
    const [profiles, setProfiles] = useState<SpreadsheetProfile[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    const loadGoogleSheetData = async (group: ProfileGroups) => {
        setError(undefined);
        setIsLoading(true);

        try {
            const { data } = await axios.get(
                `/api/accounts/v2/profiles-group/${group}`,
            );

            setProfiles(data);
        } catch (error) {
            const errorMessage = handleError(
                "Could not load the group's profiles from the spreadsheet",
                error,
                false,
            );

            setProfiles([]);
            setError(errorMessage);
        }

        setIsLoading(false);
    };

    return {
        profiles,
        fetch: loadGoogleSheetData,
        isLoading: isLoading,
        error,
    };
}

export function useAddProductsToMultipleProfiles() {
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [statusIndex, setStatusIndex] =
        useState(IMap<ProfileId, ProfileUpdateStatus>());

    const addProducts = async (
        profileId: ProfileId,
        productIds: string[],
    ): Promise<ProfileUpdateStatus> => {
        try {
            await axios.post(
                `/api/accounts/v2/profile/${profileId}/addproducts`,
                {
                    productIds: productIds,
                },
            );

            return {
                status: UpdateStatus.Updated,
                error: '',
            };
        } catch (error) {
            const errorMessage = handleError(
                `Could not update the profile`,
                error,
                false,
            );

            if (error instanceof AxiosError && error.status === 400) {
                return {
                    status: UpdateStatus.Invalid,
                    error: 'Profile ID is invalid',
                };
            }

            return {
                status: UpdateStatus.Error,
                error: errorMessage,
            };
        }
    };

    const setInitialStatusIndex = (
        initialStatusIndex: IMap<ProfileId, ProfileUpdateStatus>,
    ) => {
        if (statusIndex.size > 0) {
            return;
        }

        setStatusIndex(initialStatusIndex);
    };

    const scheduleUpdates = (profiles: ProfileId[]) => {
        setStatusIndex((prevState) =>
            prevState.merge(
                IMap(
                    profiles.map((p) => [
                        p,
                        { status: UpdateStatus.Scheduled },
                    ]),
                ),
            ),
        );
    };

    const addToMultipleProfiles = async (
        profiles: ProfileId[],
        productIds: string[],
    ) => {
        setIsUpdating(true);
        scheduleUpdates(profiles);

        for (const profileId of profiles) {
            setStatusIndex((prevState) =>
                prevState.set(profileId, { status: UpdateStatus.Updating }),
            );

            const profileUpdateResult = await addProducts(
                profileId,
                productIds,
            );

            setStatusIndex((prevState) =>
                prevState.set(profileId, profileUpdateResult),
            );
        }

        setIsUpdating(false);
    };

    return {
        addToMultipleProfiles,
        setInitialStatusIndex,
        statusIndex,
        isUpdating,
    };
}
